import { Link, NavLink, useNavigate } from "react-router-dom";
import { Heart, Home, Search, Users, ClipboardList, Calendar, MessageCircle, Settings, Mail, LogOut, BarChart3, BookOpen, Bell, Rocket, BookHeart, Archive, Compass } from "lucide-react";
import { NAV } from "@/constants/testIds";
import { useAuth } from "@/contexts/AuthContext";
import { useState, useRef, useEffect } from "react";
import FloatingCopilot from "@/components/FloatingCopilot";

const links = [
  { to: "/app", label: "Dashboard", icon: Home, id: NAV.navDashboard, end: true },
  { to: "/app/briefing", label: "Weekly Briefing", icon: BookOpen, id: NAV.navBriefing },
  { to: "/app/gmail", label: "Inbox", icon: Mail, id: NAV.navGmail },
  { to: "/app/detected", label: "Detected", icon: Search, id: NAV.navDetected },
  { to: "/app/children", label: "Children", icon: Users, id: NAV.navChildren },
  { to: "/app/tasks", label: "Tasks", icon: ClipboardList, id: NAV.navTasks },
  { to: "/app/calendar", label: "Calendar", icon: Calendar, id: NAV.navCalendar },
  { to: "/app/copilot", label: "Copilot", icon: MessageCircle, id: NAV.navCopilot },
  { to: "/app/memories", label: "Memories", icon: BookHeart, id: "nav-memories" },
  { to: "/app/yearbook", label: "Yearbook", icon: Compass, id: "nav-yearbook" },
  { to: "/app/legacy", label: "Legacy", icon: Archive, id: "nav-legacy" },
];

export default function AppShell({ family, children: kids = [], pageChildren }) {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const handleSignOut = async () => { await signOut(); navigate("/login"); };
  const initial = (user?.name || family?.parent_name || "?")[0]?.toUpperCase();

  const navLinks = [...links];
  if (user?.is_admin) {
    navLinks.push({ to: "/app/analytics", label: "Analytics", icon: BarChart3, id: "nav-analytics" });
    navLinks.push({ to: "/app/readiness", label: "Readiness", icon: Rocket, id: "nav-readiness" });
    navLinks.push({ to: "/app/admin/founder", label: "Founder", icon: BarChart3, id: "nav-founder" });
  }

  return (
    <div className="min-h-screen paper-grain">
      <header className="sticky top-0 z-30 backdrop-blur-md bg-[rgba(248,242,231,0.85)] border-b border-[var(--fcc-border)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center gap-6">
          <Link to="/app" data-testid={NAV.logo} className="flex items-center gap-3 shrink-0">
            <div className="w-11 h-11 rounded-2xl bg-[var(--fcc-ink)] text-white flex items-center justify-center relative">
              <Heart className="w-5 h-5" />
              <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-[var(--fcc-orange)] rounded-full" />
            </div>
            <div className="leading-tight">
              <div className="font-display text-[22px] tracking-tight text-[var(--fcc-green)] leading-none">KidHQ</div>
              <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--fcc-ink-soft)] mt-1">Family Command Center</div>
            </div>
          </Link>

          <div className="hidden xl:flex items-center gap-2 mr-2">
            {kids.slice(0, 4).map((k, idx) => {
              const palette = ["fcc-chip-emma", "fcc-chip-noah", "fcc-chip-peach", "fcc-chip-olive"];
              const cls = palette[idx % palette.length];
              return (
                <span key={k.id} className={`fcc-pill ${cls}`}>{k.name}</span>
              );
            })}
          </div>

          <nav className="flex-1 overflow-x-auto tab-scroll">
            <ul className="flex items-center gap-1 min-w-max">
              {navLinks.map(l => (
                <li key={l.to}>
                  <NavLink
                    to={l.to}
                    end={l.end}
                    data-testid={l.id}
                    className={({isActive}) => `flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${isActive ? "bg-[var(--fcc-ink)] text-white" : "text-[var(--fcc-ink-soft)] hover:bg-white/60"}`}
                  >
                    <l.icon className="w-4 h-4" />
                    <span>{l.label}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>

          <GlobalSearchBar />

          <div className="relative" ref={menuRef}>
            <button data-testid={NAV.userMenu} onClick={() => setMenuOpen(o => !o)} className="flex items-center gap-2 pl-1 pr-3 py-1 rounded-2xl border border-[var(--fcc-border)] bg-white hover:bg-[var(--fcc-cream-2)]">
              {user?.picture ? (
                <img src={user.picture} alt={user?.name || ""} className="w-8 h-8 rounded-xl object-cover" />
              ) : (
                <span className="w-8 h-8 rounded-xl bg-[var(--fcc-green)] text-white flex items-center justify-center text-sm font-bold">{initial}</span>
              )}
              <span className="hidden sm:inline text-sm font-semibold text-[var(--fcc-ink)] truncate max-w-[110px]">{user?.name || family?.parent_name || "You"}</span>
            </button>
            {menuOpen && (
              <div className="absolute right-0 mt-2 w-56 bg-white border border-[var(--fcc-border)] rounded-2xl shadow-lg p-2 z-50">
                <div className="px-3 py-2 text-xs text-[var(--fcc-muted)]">{user?.email}</div>
                <Link
                  to="/app/settings"
                  data-testid={NAV.navSettings}
                  onClick={() => setMenuOpen(false)}
                  className="w-full text-left px-3 py-2 rounded-xl hover:bg-[var(--fcc-cream-2)] flex items-center gap-2 text-sm font-semibold text-[var(--fcc-ink)]"
                >
                  <Bell className="w-4 h-4" /> Notification settings
                </Link>
                <button data-testid={NAV.signOut} onClick={handleSignOut} className="w-full text-left px-3 py-2 rounded-xl hover:bg-[var(--fcc-cream-2)] flex items-center gap-2 text-sm font-semibold text-[var(--fcc-ink)]">
                  <LogOut className="w-4 h-4" /> Sign out
                </button>
                <Link to="/" className="w-full text-left px-3 py-2 rounded-xl hover:bg-[var(--fcc-cream-2)] flex items-center gap-2 text-sm text-[var(--fcc-ink-soft)]">
                  <Settings className="w-4 h-4" /> Back to landing
                </Link>
              </div>
            )}
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">{pageChildren}</main>
      <footer className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 text-xs text-[var(--fcc-muted)]">
        <span>KidHQ — the AI Command Center for parents · Powered by All IT Solutions LLC · Smart people, smart solutions</span>
      </footer>
      <FloatingCopilot />
    </div>
  );
}

function GlobalSearchBar() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const onSubmit = (e) => {
    e.preventDefault();
    const term = q.trim();
    if (term.length < 2) return;
    navigate(`/app/search?q=${encodeURIComponent(term)}`);
  };
  return (
    <form onSubmit={onSubmit} data-testid="global-search-form" className="hidden md:flex items-center">
      <div className="relative">
        <Search className="w-3.5 h-3.5 text-[var(--fcc-muted)] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input
          data-testid="global-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Find anything about your family…"
          className="pl-8 pr-3 py-2 rounded-xl border border-[var(--fcc-border)] bg-white text-sm w-56 lg:w-72 focus:outline-none focus:ring-2 focus:ring-[var(--fcc-green)]/30 focus:border-[var(--fcc-green)]"
        />
      </div>
    </form>
  );
}
